var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { Check, ChevronDown, Search, AlertOctagon, CheckCircle, X } from "lucide-react";
import { cn } from "../../../lib/utils.js";
import { useDropdownListScroll } from "../../../lib/dropdown-wheel-scroll.js";
import { Popover, PopoverTrigger, PopoverContent } from "../../ui/popover.js";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip.js";
import { useVirtualizer } from "@tanstack/react-virtual";
var OptionItem = function (_a) {
    var option = _a.option, isSelected = _a.isSelected, isHighlighted = _a.isHighlighted, itemHeight = _a.itemHeight, disabled = _a.disabled, readOnly = _a.readOnly, onClick = _a.onClick, onMouseEnter = _a.onMouseEnter, onFocus = _a.onFocus, style = _a.style, className = _a.className;
    var textRef = React.useRef(null);
    var _b = React.useState(false), isHovered = _b[0], setIsHovered = _b[1];
    var _c = React.useState(false), isTruncated = _c[0], setIsTruncated = _c[1];
    // Check truncation on mouse enter - most reliable timing
    var handleMouseEnter = function () {
        var element = textRef.current;
        if (element) {
            setIsTruncated(element.scrollWidth > element.clientWidth);
        }
        setIsHovered(true);
        onMouseEnter();
    };
    var handleMouseLeave = function () {
        setIsHovered(false);
        setIsTruncated(false);
    };
    var tooltipText = option.description || (isTruncated ? option.label : "");
    var tooltipOpen = isHovered && Boolean(tooltipText);
    return (_jsxs(Tooltip, { open: tooltipOpen, children: [_jsx(TooltipTrigger, { asChild: true, children: _jsxs("button", { type: "button", role: "option", "aria-selected": isSelected, className: cn("flex flex-row justify-between items-center w-full cursor-pointer select-none rounded-[8px] px-4 py-2 text-base font-medium leading-[18px] text-[#585755] dark:text-muted-foreground outline-none transition-colors md:text-sm", isSelected && "bg-[#F4FFC6] dark:bg-primary-light text-[#242424] dark:text-foreground", isHighlighted && !isSelected && "bg-[#F8FAFC] dark:bg-accent", "hover:bg-[#F8FAFC] dark:hover:bg-accent focus:bg-[#F8FAFC] dark:focus:bg-accent", className), style: __assign({ height: "".concat(itemHeight, "px") }, style), onClick: onClick, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, onFocus: onFocus, tabIndex: 0, disabled: disabled || readOnly, children: [_jsx("span", { ref: textRef, className: "truncate text-left flex-1", children: option.label }), isSelected && (_jsx(Check, { className: "h-[18px] w-[18px] text-[#585755] dark:text-muted-foreground ml-2 flex-shrink-0" }))] }) }), _jsx(TooltipContent, { side: "right", align: "center", className: "max-w-xs", children: tooltipText })] }));
};
export var SingleSelect = function (_a) {
    var label = _a.label, _b = _a.placeholder, placeholder = _b === void 0 ? "Select..." : _b, options = _a.options, defaultValue = _a.defaultValue, _c = _a.required, required = _c === void 0 ? false : _c, _d = _a.disabled, disabled = _d === void 0 ? false : _d, _e = _a.readOnly, readOnly = _e === void 0 ? false : _e, _f = _a.freesolo, freesolo = _f === void 0 ? false : _f, onChange = _a.onChange, className = _a.className, containerClassName = _a.containerClassName, labelClassName = _a.labelClassName, fieldClassName = _a.fieldClassName, selectClassName = _a.selectClassName, hintClassName = _a.hintClassName, _g = _a.error, error = _g === void 0 ? '' : _g, _h = _a.success, success = _h === void 0 ? false : _h, _j = _a.hint, hint = _j === void 0 ? '' : _j, id = _a.id, ariaLabel = _a["aria-label"], ariaDescribedBy = _a["aria-describedby"], dataTestId = _a["data-testid"], _k = _a.virtualizationThreshold, virtualizationThreshold = _k === void 0 ? 100 : _k, _l = _a.itemHeight, itemHeight = _l === void 0 ? 40 : _l, _m = _a.maxHeight, maxHeight = _m === void 0 ? 240 : _m, maxWidth = _a.maxWidth, clearIconClassName = _a.clearIconClassName, _o = _a.portal, portal = _o === void 0 ? false : _o;
    var _p = React.useState(false), open = _p[0], setOpen = _p[1];
    var _q = React.useState(""), search = _q[0], setSearch = _q[1];
    var _r = React.useState(defaultValue), selected = _r[0], setSelected = _r[1];
    var _s = React.useState(null), highlighted = _s[0], setHighlighted = _s[1];
    var _t = React.useState(null), customValue = _t[0], setCustomValue = _t[1];
    var inputRef = React.useRef(null);
    var listRef = React.useRef(null);
    var _u = React.useState(null), listNode = _u[0], setListNode = _u[1];
    var panelRef = React.useRef(null);
    // Generate unique IDs for accessibility
    var selectId = id || "single-select-".concat(Math.random().toString(36).substr(2, 9));
    var hintId = "".concat(selectId, "-hint");
    var errorId = "".concat(selectId, "-error");
    // Filtering logic
    var filteredOptions = options.filter(function (opt) {
        return opt.label.toLowerCase().includes(search.toLowerCase());
    });
    // Should we virtualize?
    var shouldVirtualize = filteredOptions.length >= virtualizationThreshold;
    // Create virtualizer with callback ref support
    var virtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: React.useCallback(function () { return listRef.current; }, []),
        estimateSize: React.useCallback(function () { return itemHeight; }, [itemHeight]),
        overscan: 5,
    });
    // Close dropdown on page scroll (but not when scrolling inside the dropdown)
    React.useEffect(function () {
        if (!open)
            return;
        var handleScroll = function (e) {
            var _a, _b;
            var target = e.target;
            if (!target)
                return;
            // While focus is inside the panel (search input, options), ignore scroll — pasting into
            // search can resize the list and scroll a parent ScrollArea; that must not close the menu.
            if ((_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) {
                return;
            }
            // Don't close if scrolling inside the dropdown list
            if ((_b = listRef.current) === null || _b === void 0 ? void 0 : _b.contains(target)) {
                return;
            }
            // Scroll on a scrollable ancestor that contains the panel (e.g. modal ScrollArea with
            // portal={false}) — treat as layout from the open menu, not "user scrolled away".
            if (panelRef.current &&
                target instanceof Element &&
                target !== document.documentElement &&
                target !== document.body &&
                target.contains(panelRef.current)) {
                return;
            }
            setOpen(false);
        };
        // Use capture phase to catch scroll events on any scrollable element
        window.addEventListener('scroll', handleScroll, true);
        return function () {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [open]);
    // Enhanced focus effect with proper timing
    React.useEffect(function () {
        if (open) {
            setTimeout(function () {
                var _a;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 0);
        }
    }, [open]);
    React.useEffect(function () {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);
    React.useEffect(function () {
        setSelected(defaultValue);
        setCustomValue(null);
    }, [defaultValue]);
    var selectedOption = options.find(function (opt) { return opt.value === selected; });
    var isCustomSelected = !selectedOption && !!selected;
    var showError = !!error;
    var showSuccess = !!success && !showError;
    // Keyboard navigation
    var handleKeyDown = function (e) {
        var _a, _b, _c, _d;
        if (!open || disabled || readOnly)
            return;
        var idx = filteredOptions.findIndex(function (opt) { return opt.value === highlighted; });
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (filteredOptions.length === 0)
                return;
            var nextIdx = idx < filteredOptions.length - 1 ? idx + 1 : 0;
            setHighlighted((_b = (_a = filteredOptions[nextIdx]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null);
            scrollToOption(nextIdx);
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (filteredOptions.length === 0)
                return;
            var prevIdx = idx > 0 ? idx - 1 : filteredOptions.length - 1;
            setHighlighted((_d = (_c = filteredOptions[prevIdx]) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : null);
            scrollToOption(prevIdx);
        }
        else if (e.key === "Enter") {
            e.preventDefault();
            if (filteredOptions.length === 0 && search && freesolo) {
                selectCustomValue(search);
            }
            else if (highlighted) {
                selectOption(highlighted);
            }
        }
        else if (e.key === "Escape") {
            setOpen(false);
        }
    };
    var selectOption = function (value) {
        setSelected(value);
        setCustomValue(null);
        setOpen(false);
        setSearch("");
        setHighlighted(null);
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
    };
    var selectCustomValue = function (value) {
        setSelected(value);
        setCustomValue(value);
        setOpen(false);
        setSearch("");
        setHighlighted(null);
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
    };
    var clearSelection = function (e) {
        e.stopPropagation();
        setSelected(undefined);
        setCustomValue(null);
        onChange === null || onChange === void 0 ? void 0 : onChange('');
    };
    var scrollToOption = function (idx) {
        if (!listRef.current)
            return;
        var option = listRef.current.children[idx];
        if (option && typeof option.scrollIntoView === 'function') {
            option.scrollIntoView({ block: "nearest" });
        }
    };
    // Callback ref to force virtualizer recalculation when ref is set
    var setListRef = React.useCallback(function (node) {
        listRef.current = node;
        setListNode(node);
        // Force virtualizer to recalculate when ref is set
        if (node && open) {
            virtualizer.measure();
        }
    }, [virtualizer, open]);
    useDropdownListScroll(listNode, open);
    // Base classes - full width by default, constrained width only when maxWidth is provided
    var containerClasses = cn("flex flex-col items-start p-0 relative w-full", containerClassName);
    // Container style with maxWidth - only apply when explicitly provided
    var containerStyle = maxWidth !== undefined
        ? {
            width: "".concat(maxWidth, "px"),
            maxWidth: "".concat(maxWidth, "px"),
        }
        : undefined;
    var labelClasses = cn("text-[#3D3D3D] dark:text-foreground text-[14px] font-medium leading-[18px] font-[Figtree]", labelClassName);
    var fieldClasses = cn("flex flex-row items-center w-full h-[40px] rounded-[8px] border border-solid bg-white dark:bg-background px-4 py-0 text-base font-normal text-[#242424] dark:text-foreground placeholder:text-input-color focus:outline-none focus:ring-0 transition-colors md:text-sm", {
        "border-[#D90B0B] dark:border-destructive": showError,
        "border-[#009735] dark:border-success": showSuccess,
        "border-[#A09D9B] dark:border-ring": open,
        "border-[#CCC8C6] dark:border-input": !open && !showError && !showSuccess,
        "opacity-50 cursor-not-allowed bg-[#F5F5F5] dark:bg-muted": disabled,
        "bg-[#FAFAFA] dark:bg-muted": readOnly,
    }, fieldClassName);
    var hintClasses = cn("mt-1 text-[12px] font-normal leading-[16px] font-[Figtree] w-full", {
        "text-[#9E0000] dark:text-destructive": showError,
        "text-[#006623] dark:text-success-foreground": showSuccess,
        "text-[#757271] dark:text-muted-foreground": !showError && !showSuccess,
        "hidden": !hint && !showError && !showSuccess,
    }, hintClassName);
    var requiredClasses = cn("text-destructive ml-0.5", {
        "hidden": !required,
    });
    return (_jsxs("div", { className: containerClasses, style: containerStyle, "data-testid": "".concat(dataTestId, "-container"), children: [label && (_jsx("div", { className: "flex flex-row flex-wrap items-center gap-1 w-full h-[18px] mb-1", children: _jsxs("label", { htmlFor: selectId, className: labelClasses, "data-testid": "".concat(dataTestId, "-label"), children: [label, _jsx("span", { className: requiredClasses, "aria-hidden": "true", children: "*" })] }) })), _jsxs(Popover, { open: open, onOpenChange: setOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { type: "button", id: selectId, className: cn(fieldClasses, selectClassName), disabled: disabled || readOnly, tabIndex: 0, "aria-haspopup": "listbox", "aria-expanded": open, "aria-label": ariaLabel || label, "aria-invalid": showError, "aria-describedby": [
                                hint ? hintId : null,
                                showError ? errorId : null,
                                ariaDescribedBy
                            ].filter(Boolean).join(' ') || undefined, onKeyDown: handleKeyDown, onClick: function () { return !disabled && !readOnly && setOpen(function (v) { return !v; }); }, "data-testid": "".concat(dataTestId, "-field"), children: [_jsx("span", { className: cn("truncate text-left flex-1", !selectedOption && !isCustomSelected && "text-[#757271] dark:text-muted-foreground"), children: selectedOption ? selectedOption.label : isCustomSelected ? customValue : placeholder }), _jsxs("div", { className: "flex items-center gap-2 ml-2", children: [showError ? (_jsx(AlertOctagon, { className: "h-[18px] w-[18px] text-[#D90B0B] dark:text-destructive" })) : showSuccess ? (_jsx(CheckCircle, { className: "h-[18px] w-[18px] text-[#009735] dark:text-success" })) : null, selected && !disabled && !readOnly && (_jsx(X, { className: cn("h-4 w-4 text-gray-500 dark:text-muted-foreground hover:text-gray-800 dark:hover:text-foreground cursor-pointer", clearIconClassName), onClick: clearSelection })), _jsx(ChevronDown, { className: "h-[18px] w-[18px] text-[#757271] dark:text-muted-foreground" })] })] }) }), _jsxs(PopoverContent, { ref: panelRef, portal: portal, className: cn("w-[var(--radix-popover-trigger-width)] p-0 rounded-[12px] border border-[#E2E8F0] dark:border-border bg-popover !bg-[hsl(var(--popover))] shadow-[0_0_0_1px_rgba(46,43,41,0.1),0_8px_16px_-4px_rgba(46,43,41,0.03),0_8px_16px_-4px_rgba(46,43,41,0.1)] flex flex-col gap-0 max-h-[314px] overflow-hidden z-[100]"), align: "start", sideOffset: 8, onOpenAutoFocus: function (e) { return e.preventDefault(); }, children: [_jsx("div", { className: "flex items-center w-full min-w-0 box-border px-3 pt-3 pb-2", children: _jsxs("div", { "data-lift-select-search": true, className: "flex items-center w-full min-w-0 h-[34px] overflow-hidden bg-white dark:!bg-popover border border-[#E5E1DF] dark:border-[hsl(156_10%_36%)] rounded-[8px] px-2 gap-2", children: [_jsx(Search, { className: "h-[18px] w-[18px] text-[#757271] dark:text-muted-foreground shrink-0" }), _jsx("input", { ref: inputRef, type: "text", value: search, onChange: function (e) {
                                                var _a, _b;
                                                var next = e.target.value;
                                                setSearch(next);
                                                var nextFiltered = options.filter(function (opt) {
                                                    return opt.label.toLowerCase().includes(next.toLowerCase());
                                                });
                                                setHighlighted((_b = (_a = nextFiltered[0]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null);
                                            }, placeholder: placeholder, className: "flex-1 min-w-0 !bg-transparent dark:!bg-transparent outline-none !border-0 !ring-0 !shadow-none text-base font-normal text-[#757271] dark:text-foreground placeholder:text-input-color dark:placeholder:text-muted-foreground font-[Figtree] md:text-sm appearance-none", style: {
                                                fontFamily: 'Figtree',
                                                height: '34px',
                                                backgroundColor: 'transparent',
                                                boxShadow: 'none',
                                                WebkitAppearance: 'none',
                                                border: 'none',
                                                outline: 'none',
                                                minWidth: 0,
                                            }, onKeyDown: handleKeyDown, autoFocus: true, disabled: disabled || readOnly })] }) }), _jsx(TooltipProvider, { delayDuration: 300, children: _jsx("div", { ref: setListRef, role: "listbox", tabIndex: -1, className: "px-1 pb-2 min-h-0 overflow-y-auto overscroll-contain custom-scrollbar", style: {
                                        height: shouldVirtualize ? maxHeight : Math.min(maxHeight, filteredOptions.length * itemHeight),
                                        maxHeight: maxHeight,
                                        minHeight: filteredOptions.length > 0 ? itemHeight : 60,
                                    }, children: filteredOptions.length === 0 && search && freesolo ? (_jsxs("button", { type: "button", className: "flex flex-row justify-between items-center w-full cursor-pointer select-none rounded-[8px] px-4 py-2 text-base font-medium leading-[18px] text-[#585755] dark:text-muted-foreground outline-none transition-colors bg-[#F8FAFC] dark:bg-accent hover:bg-[#F4FFC6] dark:hover:bg-primary-light md:text-sm", style: { height: itemHeight }, onClick: function () { return selectCustomValue(search); }, tabIndex: 0, disabled: disabled || readOnly, children: [_jsxs("span", { className: "truncate text-left flex-1", children: ["Use \"", search, "\""] }), isCustomSelected && customValue === search && (_jsx(Check, { className: "h-[18px] w-[18px] text-[#585755] dark:text-foreground ml-2" }))] })) : filteredOptions.length === 0 ? (_jsx("div", { className: "text-center text-[#757271] dark:text-muted-foreground py-4 text-base md:text-sm", children: search ? "No options available" : "No options" })) : shouldVirtualize ? (
                                    // Virtualized rendering with safety checks
                                    _jsx("div", { style: {
                                            height: "".concat(virtualizer.getTotalSize(), "px"),
                                            width: '100%',
                                            position: 'relative',
                                            minHeight: itemHeight, // Ensure minimum height
                                        }, children: virtualizer.getVirtualItems().length > 0 &&
                                            virtualizer.getVirtualItems().map(function (virtualItem) {
                                                var opt = filteredOptions[virtualItem.index];
                                                if (!opt)
                                                    return null; // Safety check
                                                return (_jsx(OptionItem, { option: opt, isSelected: selected === opt.value, isHighlighted: highlighted === opt.value, itemHeight: itemHeight, disabled: disabled, readOnly: readOnly, onClick: function () { return selectOption(opt.value); }, onMouseEnter: function () { return setHighlighted(opt.value); }, onFocus: function () { return setHighlighted(opt.value); }, className: "absolute left-0 top-0", style: {
                                                        transform: "translateY(".concat(virtualItem.start, "px)"),
                                                    } }, opt.value));
                                            }) })) : (
                                    // Non-virtualized rendering
                                    filteredOptions.map(function (opt) { return (_jsx(OptionItem, { option: opt, isSelected: selected === opt.value, isHighlighted: highlighted === opt.value, itemHeight: itemHeight, disabled: disabled, readOnly: readOnly, onClick: function () { return selectOption(opt.value); }, onMouseEnter: function () { return setHighlighted(opt.value); }, onFocus: function () { return setHighlighted(opt.value); } }, opt.value)); })) }) })] })] }), (hint || showError || showSuccess) && (_jsx("div", { className: hintClasses, id: hint ? hintId : showError ? errorId : undefined, role: showError ? 'alert' : undefined, "data-testid": "".concat(dataTestId, "-hint"), children: showError ? error : showSuccess && hint ? hint : hint }))] }));
};
